/*
 * Live Day-of-school page — re-styled to match the Calendar / Home
 * design language: cream card on the theme bg, big orange title,
 * pill range tabs, and a hundreds chart whose cells read clearly on
 * top of the card material rather than fighting the bg art.
 */

.dayOfSchool {
  position: relative;
  z-index: 1;
  /* Edge + chrome clearance is handled by the live stage safe-area
   * (--token-safe-*); the page just fills its safe box. The old
   * hand-tuned asymmetric padding is gone (it double-inset). */
  font-family: var(--token-font-ui);
  color: var(--token-text-primary);
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
}

.empty {
  position: relative;
  z-index: 1;
  padding: var(--token-space-2xl);
  font-family: var(--token-font-ui);
}

.card {
  flex: 1;
  min-height: 0;
  /* Frosted glass — bg shows through so the chart reads as part of
   * the classroom scene, not a solid beige rectangle. */
  background: rgba(255, 250, 240, 0.45);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  border: 1.5px solid var(--token-accent);
  border-radius: 24px;
  padding: 22px clamp(20px, 2vw, 36px) 18px;
  box-shadow:
    0 30px 60px -20px rgba(8, 18, 12, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.45);
  display: flex;
  flex-direction: column;
  gap: 14px;
  /* A 10x10 chart reads best near-square, so cap width moderately and
   * let the card fill the safe box height — on a wide display the chart
   * uses vertical room and stays centered instead of stretching into a
   * short, ultra-wide band. */
  max-width: min(1500px, 100%);
  margin: 0 auto;
  width: 100%;
}

.title {
  margin: 0;
  font-family: var(--token-font-display);
  font-size: clamp(30px, 3vw, 50px);
  line-height: 1.05;
  color: var(--token-text-primary);
  text-align: center;
  /* Subtle text shadow keeps it readable on the orange-edged card. */
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.4);
}

.count {
  color: var(--token-accent);
  font-weight: 700;
}

/* Range tab strip — small pill row of 1–100, 101–200, etc. The legend
 * is visually hidden (sr-only) so the corner mascot never reads
 * "Range" peeking out from behind it. */
.rangeRow {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  border: 0;
  padding: 0;
  margin: 0 auto;
}

.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.rangeButton {
  font-family: var(--token-font-ui);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1.5px solid var(--token-accent);
  background: transparent;
  color: var(--token-accent);
  cursor: pointer;
  transition:
    background 120ms ease,
    color 120ms ease;
}

.rangeButton:hover:not([aria-pressed='true']) {
  background: color-mix(in srgb, var(--token-accent) 12%, transparent);
}

.rangeButton[aria-pressed='true'] {
  background: var(--token-accent);
  color: #fff;
}
